import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import DashboardStats from "./components/DashboardStats";
import ReservationsEnhanced from "./components/ReservationsEnhanced";
import ContactMessages from "./pages/ContactMessages/ContactMessages";
import ReservationDetails from "./pages/ReservationDetails/ReservationDetails";
import "./App.css";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div className="spinner">Loading...</div>
      </div>
    );
  }

  const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
  return isAuthenticated ? children : <Navigate to={`/login?redirect=${redirect}`} replace />;
};

// Public Route component (redirects if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div className="spinner">Loading...</div>
      </div>
    );
  }

  const params = new URLSearchParams(location.search);
  const redirectTarget = params.get("redirect");
  const safeRedirect = redirectTarget && redirectTarget.startsWith("/") ? redirectTarget : "/dashboard/overview";

  return !isAuthenticated ? children : <Navigate to={safeRedirect} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<DashboardStats />} />
        <Route path="reservations" element={<ReservationsEnhanced />} />
        <Route path="reservations/:id" element={<ReservationDetails />} />
        <Route path="contact" element={<ContactMessages />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard/overview" replace />} />
      
      {/* 404 - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard/overview" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
